import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Container, Image, Skeleton, useToast, VStack } from "@chakra-ui/react";
import MetaTags from "../../../layouts/HelmetTags";
import "./ScrollIndicator.css";


function PreRegistration(): React.JSX.Element {
  const toast = useToast();
  const location = useLocation();

  const [isFirstImageLoaded, setIsFirstImageLoaded] = useState(false);
  const [isSecondImageLoaded, setIsSecondImageLoaded] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  // 첫 번째 이미지 프리로딩
  useEffect(() => {
    const firstImage: HTMLImageElement = new window.Image();
    firstImage.src = "/pre-registration/safe-refund-pre-registration-1-2_1800px.png?version=3";
    firstImage.onload = () => setIsFirstImageLoaded(true);
  }, []);

  // 첫 번째 이미지 로드 후 두 번째 이미지 프리로딩
  useEffect(() => {
    if (isFirstImageLoaded) {
      const secondImage: HTMLImageElement = new window.Image();
      secondImage.src = "/pre-registration/safe-refund-pre-registration-2-3_1800px.png?version=4";
      secondImage.onload = () => setIsSecondImageLoaded(true);
    }
  }, [isFirstImageLoaded]);

  // URL에서 referral 코드 가져오기
  const getReferralCode = () => {
    const searchParams = new URLSearchParams(location.search);
    const referral = searchParams.get("referral") || "";
    const device = searchParams.get("device") || "";
    let isAndroid = false;
    if (device === "android") {
      isAndroid = true;
    }

    // 6자리 검증
    const isValidReferral = /^.{6}$/.test(referral);

    return {
      code: referral,
      isValid: isValidReferral,
      isAndroid,
    };
  };

  const showSuccessToast = () => {
    toast({
      position: "top",
      duration: 2000,
      isClosable: true,
      render: () => (
        <Box
          width="227px"
          height="28px"
          m="auto auto"
          bg="rgba(0, 0, 0, 0.50)"
          color="#A9FFF9"
          borderRadius="10px"
          fontSize="11px"
          fontStyle="normal"
          fontWeight="400"
          lineHeight="18px"
          letterSpacing="-0.22px"
          textAlign="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          초대코드가 복사되었습니다
        </Box>
      ),
    });
  };

  const showErrorToast = () => {
    toast({
      position: "top",
      duration: 2000,
      render: () => (
        <Box
          width="227px"
          height="28px"
          m="auto auto"
          bg="rgba(0, 0, 0, 0.50)"
          color="#FF7171"
          borderRadius="10px"
          fontSize="11px"
          fontStyle="normal"
          fontWeight="400"
          lineHeight="18px"
          letterSpacing="-0.22px"
          textAlign="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          초대링크가 잘못되었습니다
        </Box>
      ),
    });
  };

  // 클립보드 복사 함수
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast();
    } catch (err) {
      showErrorToast();
    }
  };

  // 초대코드 복사 핸들러
  const handleCopyClick = async () => {
    const { code, isValid } = getReferralCode();
    if (isValid) {
      await copyToClipboard(code);
    } else {
      showErrorToast();
    }
  };

  // 간식 뽑으러 가기 핸들러
  const handleGoToSnack = async () => {
    const { code, isValid, isAndroid } = getReferralCode();
    if (isValid) {
      // if (isAndroid) {
      // 무조건 실행
      const appScheme = "pluscompany://open";
      const appLink = "https://plus-api-prod.com/plusLink/safety_refund_event";

      // 시작 시간 기록
      const startTime = new Date().getTime();

      // visibility change 이벤트 리스너
      const visibilityHandler = () => {
        document.removeEventListener("visibilitychange", visibilityHandler);
        clearTimeout(timeoutId);
      };

      document.addEventListener("visibilitychange", visibilityHandler);

      // App Schema 시도
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = appScheme;
      document.body.appendChild(iframe);

      // 500ms 후에 앱 링크로 이동
      const timeoutId = setTimeout(() => {
        const currentTime = new Date().getTime();
        if (currentTime - startTime > 500 && !document.hidden) {
          document.body.removeChild(iframe);
          window.location.href = appLink;
        }
      }, 500);
      // } else {
      //   await copyToClipboard(code);
      //   window.location.href = "https://plus-backend-prod-u7xx3xjh7a-du.a.run.app/plusLink/safety_refund_event";
      // }
    } else {
      showErrorToast();
    }
  };

  // 스크롤 위치 체크
  useEffect(() => {
    const handleScroll = () => {
      // 현재 스크롤 위치
      const currentScroll = window.scrollY + window.innerHeight;
      // 전체 높이
      const totalHeight = document.documentElement.scrollHeight;
      // 하단에서 100px 정도 위치에 도달하면 화살표 숨기기
      setShowScrollIndicator(currentScroll < totalHeight - 100);
    };
    window.addEventListener("scroll", handleScroll);
    // 초기 스크롤 상태 체크
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const buttonCommonStyles = {
    width: "85%",
    height: "45px",
    boxShadow: "0 3px 3px rgba(0, 0, 0, 0.25)",
    borderRadius: "12px",
    bottom: "20px",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "18px",
    letterSpacing: "-0.28px",
    transition: "all 0.2s ease",
  };

  const codeButtonStyles = {
    ...buttonCommonStyles,
    color: "#2DC1BD",
    background: "linear-gradient(180deg, #EAFFF9 0%, #AEEBE6 100%)",
    _hover: {
      opacity: 0.95,
      background: "linear-gradient(180deg, #F2FFFC 0%, #BFEEE9 100%)",
    },
    _active: {
      opacity: 0.95,
      background: "linear-gradient(180deg, #F2FFFC 0%, #BFEEE9 100%)",
    },
    _focus: {
      opacity: 0.95,
      background: "linear-gradient(180deg, #F2FFFC 0%, #BFEEE9 100%)",
    },
  };

  const pickButtonStyles = {
    ...buttonCommonStyles,
    color: "#FCFFFF",
    background: "linear-gradient(180deg, #61E4E1 0%, #2DC1BD 100%)",
    _hover: {
      opacity: 0.95,
      background: "linear-gradient(180deg, #70E8E5 0%, #33C9C5 100%)",
    },
    _active: {
      opacity: 0.95,
      background: "linear-gradient(180deg, #70E8E5 0%, #33C9C5 100%)",
    },
    _focus: {
      opacity: 0.95,
      background: "linear-gradient(180deg, #70E8E5 0%, #33C9C5 100%)",
    },
  };

  return (
    <>
      <MetaTags
        title="덧셈 안전환급 사전신청 이벤트"
        description="이 링크로 접속하면 두 번 뽑을 수 있어요!"
        image="https://plus-603842133488.asia-northeast3.run.app/pre-registration/pre-registration-invite.png"
        url="https://plus-603842133488.asia-northeast3.run.app/safe-refund/pre-registration/"
      />
      <Box
        w="100%"
        minH="100vh"
        bg="white"
        display="flex"
        justifyContent="center"
        pb="95px"
      >
        <Container
          maxW="450px"
          p={0}
          pt="env(safe-area-inset-top)" // iOS 노치 영역만큼 상단 패딩 추가
          display="flex"
          flexDirection="column"
          width="100%"
          alignItems="center"
        >
          <VStack width="100%" align="stretch" alignItems="center">
            {!isFirstImageLoaded ? (
              <Skeleton
                roundedBottom="10px"
                width="100%"
                height="600px"
              />
            ) : (
              <Image
                src="/pre-registration/safe-refund-pre-registration-1-2_1800px.png"
                alt="First content"
                width="100%"
                height="auto"
                loading="eager"
                objectFit="cover"
              />
            )}
            {!isSecondImageLoaded ? (
              <Skeleton
                roundedTop="10px"
                width="100%"
                height="200px"
              />
            ) : (
              <Image
                src="/pre-registration/safe-refund-pre-registration-2-3_1800px.png"
                alt="Second content"
                width="100%"
                height="auto"
                loading="eager"
                objectFit="cover"
              />
            )}
          </VStack>
        </Container>
      </Box>
      <div className="fixed-bottom-container">
        {showScrollIndicator && (
          <a className="scroll-indicator">
            <span></span>
          </a>
        )}
        <div className="buttons-container">
          <Button sx={codeButtonStyles} onClick={handleCopyClick}>초대코드 복사</Button>
          <Button sx={pickButtonStyles} onClick={handleGoToSnack}>간식 뽑으러 가기</Button>
        </div>
      </div>
    </>
  );
}

export default PreRegistration;